import _, {chain, difference, first, isEqual, random, union, without} from 'lodash';
import {DateTime, DurationLikeObject} from 'luxon';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ErrorWithData, ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {atIndex} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {
  buildCalendarEventVersionUrl,
  buildDraftCalendarEventVersion,
  isEqualOrSubsetCalendarParameters,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {
  buildCalendarEventVersionStartEnd,
  sortedCalendarColors,
  StartEndData,
} from '../../../helpers/calendarHelpers';
import {
  buildCalendarEvent,
  findDefaultEventStartForFocusedDate,
} from '../../../helpers/models/calendarEventHelpers';
import {
  buildParticipantFromVisualParticipant,
  buildVisualParticipantFromVisualContact,
  maybeAddOrganizerToVisualParticipants,
  tryParseVisualParticipantFromText,
} from '../../../helpers/models/participantHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {ApiCalendarEventVersionPageDataGroup} from '../../../models/api/dataGroupModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {fetchCalendarForOrganizerAddressAsync} from '../../../services/calendar/findCalendar';
import {fetchApiAsync} from '../../../services/http/http';
import {findPanelSequence} from '../../../services/panels';
import {buildPanelCancelToken} from '../../../services/panelsCancellers';
import {CalendarTypesEnum, InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {
  calendarVersionForcedOnceTimeSyncAction,
  updateCalendarVersionAction,
} from '../../data/calendarVersions/calendarVersionsActions';
import {
  getComposeCalendarVersion,
  getFlatCalendarVersions,
  getSelectedCalendarVersions,
} from '../../data/calendarVersions/calendarVersionsSelectors';
import {getCompany} from '../../data/company/companySelectors';
import {updateDataAction} from '../../data/dataGroupsActions';
import {getCalendars, getCalendarVersions} from '../../data/dataSelectors';
import {updateSelectedSideFrameIdAction} from '../../data/localPreferences/localPreferencesActions';
import {getCalendarWeekStartDay} from '../../data/preferences/preferencesSelectors';
import {getUser, getUserNamespace} from '../../data/teammates/teammatesSelectors';
import {makeGetAppPanel} from '../app/appPanelSelectors';
import {AppContentTypesEnum} from '../app/parameters/appParameters';
import {PanelCancelTokenId} from '../panelCancelTokenId';
import {PanelActionPayload, setPanelAction} from '../panelsActions';
import {getFirstAppPanelId, getFlatPanels, getPanels, makeGetAppPanelIdFromPanel} from '../panelsSelectors';
import {PanelsState} from '../panelsState';
import {
  CalendarsPanelId,
  isAppCalendarsPanelId,
  isCalendarsSidePanelId,
  makeCalendarsPanelId,
  PanelTypesEnum,
} from '../panelStateBase';
import {
  CalendarEventData,
  getCalendarSidePanel,
  getIsSidePanelCalendarVisible,
  makeGetCalendar,
  makeGetCalendarsPanel,
  makeGetCalendarsParameters,
} from './calendarsPanelSelectors';
import {
  CalendarEventGridParameters,
  CalendarEventOverrides,
  CalendarEventParameters,
  CalendarsPanelState,
  CalendarViewShiftDirectionsEnum,
  FocusedEventSourcesEnum,
  ViewingNowCalendar,
} from './calendarsPanelState';
import {CalendarsParameters, CalendarsViewTypesEnum} from './calendarsParameters';
import {findParametersCalendarVersionIds, findParametersRange} from './calendarsParametersHelpers';
import {CalendarsPanelSchedulingAction} from './scheduling/calendarsPanelSchedulingActions';

/*
 * Type enum.
 */

export enum CalendarsPanelActionsEnum {
  NAVIGATE_CALENDARS = '@@panels/calendars/NAVIGATE_CALENDARS',
  FETCH_CALENDAR_EVENT_VERSIONS_COMPLETED = '@@panels/calendars/FETCH_CALENDAR_EVENT_VERSIONS_COMPLETED',
  FETCH_CALENDAR_EVENT_VERSIONS_FAILED = '@@panels/calendars/FETCH_CALENDAR_EVENT_VERSIONS_FAILED',
  HIGHLIGHT_CALENDAR_EVENT = '@@panels/calendars/HIGHLIGH_CALENDAR_EVENT',
  FOCUS_CALENDAR_EVENT = '@@panels/calendars/FOCUS_CALENDAR_EVENT',
  POP_CALENDAR_EVENT = '@@panels/calendars/POP_CALENDAR_EVENT',
  UPDATE_VISIBLE_CALENDARS = '@@panels/calendars/UPDATE_VISIBLE_CALENDARS',
  ADD_CALENDAR_ADDRESS = '@@panels/calendars/ADD_CALENDAR_ADDRESS',
}

/*
 * Types.
 */

type CalendarsPanelActionPayload = PanelActionPayload<CalendarsPanelId>;

interface NavigateCalendarsActionPayload extends CalendarsPanelActionPayload {
  parameters: CalendarsParameters;
  cancelTokenId: PanelCancelTokenId;
}

interface NavigateCalendarsAction extends ActionWithPayload<NavigateCalendarsActionPayload> {
  type: CalendarsPanelActionsEnum.NAVIGATE_CALENDARS;
}

export interface FetchCalendarEventVersionsPayload extends CalendarsPanelActionPayload {
  parameters: CalendarsParameters;
}

interface FetchCalendarEventVersionsCompletedContext extends FetchCalendarEventVersionsPayload {
  calendarEventVersionUids: ReadonlyArray<string>;
  maxUpdatedAt: number | undefined;
}

export interface FetchCalendarEventVersionsCompletedAction
  extends ActionWithPayload<FetchCalendarEventVersionsCompletedContext> {
  type: CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_COMPLETED;
}

export interface FetchCalendarEventVersionsFailedAction
  extends ActionWithPayload<FetchCalendarEventVersionsPayload> {
  type: CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_FAILED;
}

interface CalendarEventActionPayload extends CalendarsPanelActionPayload {
  eventParameters: CalendarEventGridParameters | undefined;
}

interface HighlightCalendarEventAction extends ActionWithPayload<CalendarEventActionPayload> {
  type: CalendarsPanelActionsEnum.HIGHLIGHT_CALENDAR_EVENT;
}

interface FocusCalendarEventAction extends ActionWithPayload<CalendarEventActionPayload> {
  type: CalendarsPanelActionsEnum.FOCUS_CALENDAR_EVENT;
}

interface PopCalendarEventAction extends ActionWithPayload<CalendarEventActionPayload> {
  type: CalendarsPanelActionsEnum.POP_CALENDAR_EVENT;
}

interface VisibleCalendarsActionPayload extends CalendarsPanelActionPayload {
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  selectedCalendarVersionIds: ReadonlyArray<number>;
}

interface UpdateVisibleCalendarsAction extends ActionWithPayload<VisibleCalendarsActionPayload> {
  type: CalendarsPanelActionsEnum.UPDATE_VISIBLE_CALENDARS;
}

interface AddCalendarAddressActionPayload extends CalendarsPanelActionPayload {
  address: string;
  isPrimary: boolean;
}

interface AddCalendarAddressAction extends ActionWithPayload<AddCalendarAddressActionPayload> {
  type: CalendarsPanelActionsEnum.ADD_CALENDAR_ADDRESS;
}

/*
 * Action creators.
 */

interface NavigateExistingCalendarsOptions {
  alwaysRefetch?: boolean;
}

// TODO: Merge with navigateCalendarsAction().
export function navigateExistingCalendarsAction(
  panelId: CalendarsPanelId,
  parameters: CalendarsParameters,
  {alwaysRefetch}: NavigateExistingCalendarsOptions = {},
): ThunkAction {
  return (dispatch, getState) => {
    // Find the Calendars panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.CALENDARS) {
      throw new Error('Wrong panel type.');
    }

    const previousParameters = panel?.parameters;

    if (!alwaysRefetch) {
      // If the panel exists and has the same parameters, nothing to do.
      if (previousParameters && isEqual(previousParameters, parameters)) {
        return undefined;
      }

      // If the panel exists and the new parameters are a subset of the existing one, only navigate.
      if (panel && previousParameters && isEqualOrSubsetCalendarParameters(previousParameters, parameters)) {
        const {navigateAction} = buildNavigateActionData(panel, parameters);
        dispatch(navigateAction);
        return undefined;
      }
    }

    // If the panel exists with different parameters, navigate and also fetch updated data.
    if (panel) {
      const {navigateAction, cancelToken} = buildNavigateActionData(panel, parameters);
      return batch(() => {
        dispatch(navigateAction);
        // Fetch the events for the new parameters.
        const fetchAction = fetchCalendarEventsAction(panelId, parameters, cancelToken);
        return dispatch(fetchAction);
      });
    }

    // TEMPORARY: This is be removed with the Redux-based routing.
    throw new Error('The panel should already exist.');
  };
}

export function navigateCalendarsAction(
  panelId: CalendarsPanelId,
  parametersOverrides?: Partial<CalendarsParameters>,
  panelOverrides?: Partial<CalendarsPanelState>,
  newEventContacts?: ReadonlyArray<VisualContactModel>,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const selectedCalendarVersionIds = getSelectedCalendarVersions(state).map((v) => v.id);
    const parameters: CalendarsParameters = {
      viewType: CalendarsViewTypesEnum.WEEK,
      focusedDate: Date.now(),
      weekStartDay: getCalendarWeekStartDay(state),
      selectedCalendarVersionIds,
      viewingNowCalendarVersionIds: [],
      ...parametersOverrides,
    };

    const [cancelTokenId, cancelToken] = buildPanelCancelToken(panelId);
    const fetchAction = fetchCalendarEventsAction(panelId, parameters, cancelToken);

    dispatch(
      setPanelAction({
        id: panelId,
        type: PanelTypesEnum.CALENDARS,
        parameters,
        status: AsyncStatusesEnum.LOADING,
        calendarEventVersionUids: [],
        maxUpdatedAt: undefined,
        cancelTokenId,
        highlightedCalendarEventParameters: undefined,
        focusedCalendarEventParameters: undefined,
        popoverCalendarEventParameters: undefined,
        viewingNowCalendars: [],
        sequence: findPanelSequence(),
        scheduling: undefined,
        ...panelOverrides,
      }),
    );
    dispatch(fetchAction);

    // If we are supplied new event contacts, we should create a new draft event.
    if (newEventContacts) {
      dispatch(createOrUpdateCalendarEventDraftAction(panelId, newEventContacts));
    }
  };
}

export function popoutSideCalendarsAction(panelId: CalendarsPanelId): ThunkAction {
  return (dispatch, getState) => {
    const popupPanelId = makeCalendarsPanelId();
    const popupAppPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});

    const {focusedDate, selectedCalendarVersionIds} = makeGetCalendarsParameters()(getState(), {panelId});
    const newParameters: Partial<CalendarsParameters> = {focusedDate, selectedCalendarVersionIds};

    dispatch(
      navigateCalendarsAction(popupPanelId, newParameters, {
        popupAppPanelId,
        shouldForcePopout: true,
      }),
    );
  };
}

export function fetchCalendarEventsAction(
  panelId: CalendarsPanelId,
  parameters: CalendarsParameters,
  cancelToken: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const calendarVersionIds = findParametersCalendarVersionIds(parameters);

    // If there are no CalendarVersions in the parameters, return right away.
    if (calendarVersionIds.length === 0) {
      const completedAction: FetchCalendarEventVersionsCompletedAction = {
        type: CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_COMPLETED,
        payload: {
          panelId,
          parameters,
          calendarEventVersionUids: [],
          maxUpdatedAt: 0,
        },
      };
      dispatch(completedAction);
      return;
    }

    // Find the panel.
    const state = getState();
    const panel = makeGetCalendarsPanel()(state, {panelId});

    // Prepare the request.
    const {startsAt, endsAt} = findParametersRange(parameters);
    const query = {
      range_start: new Date(startsAt).toISOString(),
      range_end: new Date(endsAt).toISOString(),
      calendar_version_ids: calendarVersionIds.join(','),
      min_updated_at: panel.maxUpdatedAt && new Date(panel.maxUpdatedAt).toISOString(),
    };

    const company = getCompany(state);
    const user = getUser(state);
    const url = addQueryToPath(`${company.url}/namespaces/${user.namespace}/calendar_event_versions`, query);

    // Perform the request.
    try {
      const fetchDataGroup = await fetchApiAsync(ApiCalendarEventVersionPageDataGroup, url, {cancelToken});

      batch(() => {
        // Add all the data to the store.
        dispatch(updateDataAction(fetchDataGroup));

        // Set maxUpdatedAt by using the updatedAt of the most recently updated calendarEventVersion
        // in this view. If events from this same view need to be re-fetched, maxUpdatedAt
        // will be included as a minUpdated query parameter.
        const maxUpdatedAt = _(fetchDataGroup.calendarEventVersions)
          .map((v) => v.updatedAt)
          .max();

        const completedAction: FetchCalendarEventVersionsCompletedAction = {
          type: CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_COMPLETED,
          payload: {
            panelId,
            parameters,
            calendarEventVersionUids: _(fetchDataGroup.calendarEventVersions || [])
              .map((v) => v.uid)
              .uniq()
              .value(),
            maxUpdatedAt,
          },
        };
        dispatch(completedAction);
      });
    } catch (error) {
      // If this is a cancellation error, nothing to do.
      if (error instanceof CancelError) {
        return;
      }

      const failedAction: FetchCalendarEventVersionsFailedAction = {
        type: CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_FAILED,
        payload: {panelId, parameters},
      };
      dispatch(failedAction);
    }
  };
}

export function refreshCalendarPanelsAsyncAction(updatedCalendarVersionId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const panels = getFlatPanels(getState());

    try {
      const refreshPromises = _(panels)
        .map((p) => p.type === PanelTypesEnum.CALENDARS && p)
        .compact()
        .filter((p) => {
          const calendarVersionIds = findParametersCalendarVersionIds(p.parameters);
          return calendarVersionIds.includes(updatedCalendarVersionId);
        })
        .map(({id, parameters}) =>
          dispatch(navigateExistingCalendarsAction(id, parameters, {alwaysRefetch: true})),
        )
        .value();

      await Promise.all(refreshPromises);
    } catch (error) {
      reportToBugsnag(new ExtendedError('Refreshing calendars failed', error));
    }
  };
}

export function updateCalendarViewTypeAction(
  panelId: CalendarsPanelId,
  viewType: CalendarsViewTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    // Build the parameters with the new ViewType.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const newParameters: CalendarsParameters = {...parameters, viewType};

    // Navigate to the new parameters.
    return dispatch(navigateExistingCalendarsAction(panelId, newParameters));
  };
}

export function updateCalendarFocusedDateAction(panelId: CalendarsPanelId, focusedDate: number): ThunkAction {
  return (dispatch, getState) => {
    // Build the parameters with the new FocusedDate.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const newParameters: CalendarsParameters = {...parameters, focusedDate};

    // Navigate to the new parameters.
    return dispatch(navigateExistingCalendarsAction(panelId, newParameters));
  };
}

export function updateCalendarWeekStartDayAction(
  panelId: CalendarsPanelId,
  weekStartDay: CalendarWeekDaysEnum,
): ThunkAction {
  return (dispatch, getState) => {
    // Build the parameters with the new Week Start Day.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const newParameters: CalendarsParameters = {...parameters, weekStartDay};

    // Navigate to the new parameters.
    return dispatch(navigateExistingCalendarsAction(panelId, newParameters));
  };
}

export function addCalendarAddressAction(
  panelId: CalendarsPanelId,
  address: string,
  isPrimary: boolean = true,
): AddCalendarAddressAction {
  return {
    type: CalendarsPanelActionsEnum.ADD_CALENDAR_ADDRESS,
    payload: {panelId, address, isPrimary},
  };
}

const viewTypeToShiftDelta: {[K in CalendarsViewTypesEnum]: DurationLikeObject} = {
  [CalendarsViewTypesEnum.DAY]: {day: 1},
  [CalendarsViewTypesEnum.WEEK]: {week: 1},
  [CalendarsViewTypesEnum.MONTH]: {month: 1},
  [CalendarsViewTypesEnum.PREVIEW]: {},
  [CalendarsViewTypesEnum.SCHEDULING]: {week: 1},
  [CalendarsViewTypesEnum.REMINDERS]: {},
};

export function shiftCalendarViewAction(
  panelId: CalendarsPanelId,
  direction: CalendarViewShiftDirectionsEnum,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the current focused date in the panel.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const focusedDateTime = DateTime.fromMillis(parameters.focusedDate);

    // Compute the delta to apply and build the new focused date.
    const delta = viewTypeToShiftDelta[parameters.viewType];
    const newFocusedDateTime =
      direction === CalendarViewShiftDirectionsEnum.NEXT
        ? focusedDateTime.plus(delta)
        : focusedDateTime.minus(delta);

    // Navigate to the new focused date.
    dispatch(updateCalendarFocusedDateAction(panelId, newFocusedDateTime.valueOf()));
  };
}

export function toggleSelectedCalendarVersionAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
  shouldSaveCalendarVersion: boolean = true,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the currently selected CalendarVersion IDs.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const {selectedCalendarVersionIds} = parameters;

    // Build the new array of CalendarVersion IDs based on the specified ID.
    const wasSelected = selectedCalendarVersionIds.includes(calendarVersionId);
    return dispatch(
      wasSelected
        ? unselectCalendarVersionAction(panelId, calendarVersionId, shouldSaveCalendarVersion)
        : selectCalendarVersionAction(panelId, calendarVersionId, shouldSaveCalendarVersion),
    );
  };
}

export function selectCalendarVersionAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
  shouldSaveCalendarVersion: boolean = true,
): ThunkAction {
  return (dispatch, getState) => {
    dispatch(
      updateSelectedCalendarVersionAction(panelId, calendarVersionId, true, shouldSaveCalendarVersion),
    );

    const getCalendar = makeGetCalendar(() => calendarVersionId);
    const calendar = getCalendar(getState(), undefined);

    if (!calendar || !calendar.organizerAddress) {
      return;
    }

    // We want to force sync the calendarVersion to make sure we have up-to-date events.
    if (!shouldSaveCalendarVersion) {
      dispatch(calendarVersionForcedOnceTimeSyncAction(calendarVersionId));
    }

    dispatch(addCalendarAddressAction(panelId, calendar.organizerAddress, calendar.isPrimaryForOrganizer));
  };
}

export function unselectCalendarVersionAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
  shouldSaveCalendarVersion: boolean = true,
): ThunkAction {
  return updateSelectedCalendarVersionAction(panelId, calendarVersionId, false, shouldSaveCalendarVersion);
}

export function updateSelectedCalendarVersionAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
  isSelected: boolean,
  shouldSaveCalendarVersion: boolean = true,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the currently selected CalendarVersion IDs.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const {selectedCalendarVersionIds} = parameters;

    // Build the new array of CalendarVersion IDs based on the specified ID.
    const newSelectedCalendarVersionIds = isSelected
      ? union(selectedCalendarVersionIds, [calendarVersionId])
      : without(selectedCalendarVersionIds, calendarVersionId);

    // Build the new parameters with the new IDs.
    const newParameters: CalendarsParameters = {
      ...parameters,
      selectedCalendarVersionIds: newSelectedCalendarVersionIds,
    };

    if (shouldSaveCalendarVersion) {
      // Save the Calendar Version.
      dispatch(updateCalendarVersionAction(calendarVersionId, {isSelected}));
    }

    // Navigate to the new parameters.
    return dispatch(navigateExistingCalendarsAction(panelId, newParameters));
  };
}

export function selectUniqueCalendarVersionTransientAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
): ThunkAction {
  return (dispatch, getState) => {
    // Build the parameters with the new Selected CalendarVersion IDs.
    const parameters = makeGetCalendarsParameters()(getState(), {panelId});
    const newParameters: CalendarsParameters = {
      ...parameters,
      selectedCalendarVersionIds: [calendarVersionId],
    };

    // Navigate to the new parameters.
    return dispatch(navigateExistingCalendarsAction(panelId, newParameters));
  };
}

export function highlightCalendarEventAction(
  panelId: CalendarsPanelId,
  eventParameters: CalendarEventGridParameters | undefined,
): HighlightCalendarEventAction {
  return {
    type: CalendarsPanelActionsEnum.HIGHLIGHT_CALENDAR_EVENT,
    payload: {
      panelId,
      eventParameters,
    },
  };
}

export function focusCalendarEventAction(
  panelId: CalendarsPanelId,
  eventParameters: CalendarEventGridParameters | undefined,
): FocusCalendarEventAction {
  return {
    type: CalendarsPanelActionsEnum.FOCUS_CALENDAR_EVENT,
    payload: {
      panelId,
      eventParameters,
    },
  };
}

export function popCalendarEventAction(
  panelId: CalendarsPanelId,
  eventParameters: CalendarEventGridParameters | undefined,
): PopCalendarEventAction {
  return {
    type: CalendarsPanelActionsEnum.POP_CALENDAR_EVENT,
    payload: {
      panelId,
      eventParameters,
    },
  };
}

export function clearDeletedEventParametersAction(
  panelId: CalendarsPanelId,
  eventData: CalendarEventData,
): ThunkAction {
  return (dispatch, getState) => {
    const calendarsPanel = makeGetCalendarsPanel()(getState(), {panelId});

    batch(() => {
      if (parametersMatchData(eventData, calendarsPanel.highlightedCalendarEventParameters)) {
        dispatch(highlightCalendarEventAction(panelId, undefined));
      }

      if (parametersMatchData(eventData, calendarsPanel.focusedCalendarEventParameters)) {
        dispatch(focusCalendarEventAction(panelId, undefined));
      }

      if (parametersMatchData(eventData, calendarsPanel.popoverCalendarEventParameters)) {
        dispatch(popCalendarEventAction(panelId, undefined));
      }
    });
  };
}

function parametersMatchData(
  calendarEventData: CalendarEventData,
  eventParameters: CalendarEventGridParameters | undefined,
) {
  if (!eventParameters) {
    return false;
  }

  return (
    eventParameters.calendarEventVersionUid === calendarEventData.calendarEventVersion.uid &&
    eventParameters.calendarVersionId === calendarEventData.calendarVersion.id &&
    eventParameters.instanceStartDate === calendarEventData.instanceStartDate
  );
}

export function createOrUpdateCalendarEventDraftAction(
  panelId: CalendarsPanelId,
  newEventContacts?: ReadonlyArray<VisualContactModel>,
): ThunkAction {
  return (dispatch, getState) => {
    const {focusedDate} = makeGetCalendarsParameters()(getState(), {panelId});
    const nextHourStart = findDefaultEventStartForFocusedDate(focusedDate);

    dispatch(
      createOrUpdateCalendarEventDraftFromSlotAction(
        panelId,
        {
          startDateTime: nextHourStart,
          endDateTime: nextHourStart.plus({minutes: 30}),
          isAllDay: false,
        },
        newEventContacts,
      ),
    );
  };
}

export function updateCalendarEventDraftAction(
  panelId: CalendarsPanelId,
  overridesPatch: CalendarEventOverrides,
): ThunkAction {
  return (dispatch, getState) => {
    const {focusedCalendarEventParameters} = makeGetCalendarsPanel()(getState(), {panelId});
    if (!focusedCalendarEventParameters) {
      return;
    }

    const newOverrides: CalendarEventOverrides = {
      ...focusedCalendarEventParameters.overrides,
      ...overridesPatch,
    };

    dispatch(
      focusCalendarEventAction(panelId, {
        ...focusedCalendarEventParameters,
        overrides: newOverrides,
      }),
    );
  };
}

export function createOrUpdateCalendarEventDraftFromSlotAction(
  panelId: CalendarsPanelId,
  slot: StartEndData,
  newEventContacts?: ReadonlyArray<VisualContactModel>,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const composeCalendarVersion = getComposeCalendarVersion(state);
    if (!composeCalendarVersion) {
      return;
    }

    const composeCalendar = getCalendars(state)[composeCalendarVersion.calendarId];
    if (!composeCalendar) {
      return;
    }

    // If we already have a focused calendar event, just update the event overrides instead of creating a new draft.
    const {focusedCalendarEventParameters} = makeGetCalendarsPanel()(state, {panelId});
    if (focusedCalendarEventParameters) {
      dispatch(updateCalendarEventDraftAction(panelId, {startEnd: buildCalendarEventVersionStartEnd(slot)}));
      return;
    }

    // Otherwise, create a new event.
    dispatch(
      createCalendarEventDraftAction(
        panelId,
        composeCalendarVersion.id,
        slot.startDateTime,
        slot.endDateTime,
        slot.isAllDay,
        composeCalendar.organizerAddress,
        newEventContacts,
      ),
    );
  };
}

function createCalendarEventDraftAction(
  panelId: CalendarsPanelId,
  calendarVersionId: number,
  startDateTime: DateTime,
  endDateTime: DateTime,
  isAllDay: boolean,
  organizerAddress: string | undefined,
  newEventContacts?: ReadonlyArray<VisualContactModel>,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    // Build the calendar event.
    const {viewingNowCalendars} = makeGetCalendarsPanel()(state, {panelId});
    const visualParticipants = buildInitialVisualParticipants(
      viewingNowCalendars,
      organizerAddress,
      newEventContacts,
    );
    const participants = visualParticipants.map(buildParticipantFromVisualParticipant);
    const contacts = visualParticipants.map((p) => p.contact);

    const calendarEventVersionUid = randomString();
    const calendarEvent = buildCalendarEvent(calendarEventVersionUid, participants);
    const calendarEventVersionUrl = buildCalendarEventVersionUrl(state.data.company, calendarEventVersionUid);

    // Build the calendar event version.
    const calendarEventVersion = buildDraftCalendarEventVersion(
      calendarEventVersionUid,
      calendarEventVersionUrl,
      calendarEvent.uid,
      calendarVersionId,
      startDateTime,
      endDateTime,
      isAllDay,
    );

    const parameters: CalendarEventGridParameters = {
      calendarEventVersionUid,
      calendarVersionId,
      instanceStartDate: undefined,
      source: FocusedEventSourcesEnum.CALENDAR_GRID,
      overrides: {},
    };

    batch(() => {
      dispatch(
        updateDataAction({
          calendarEvents: [calendarEvent],
          calendarEventVersions: [calendarEventVersion],
          contacts,
        }),
      );

      dispatch(focusCalendarEventAction(panelId, parameters));
    });
  };
}

function buildInitialVisualParticipants(
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
  organizerAddress: string | undefined,
  newEventContacts?: ReadonlyArray<VisualContactModel>,
) {
  const viewingNowVisualParticipants = _(viewingNowCalendars)
    .map((c) => tryParseVisualParticipantFromText(c.address))
    .compact()
    .value();

  const contactParticipants = newEventContacts?.map(buildVisualParticipantFromVisualContact) ?? [];

  if (viewingNowVisualParticipants.length === 0 && contactParticipants.length === 0) {
    return [];
  }

  return maybeAddOrganizerToVisualParticipants(
    [...viewingNowVisualParticipants, ...contactParticipants],
    organizerAddress,
  );
}

export function openCalendarEventAction(
  focusedDate: number,
  eventParameters: CalendarEventParameters,
): ThunkAction {
  return (dispatch, getState) => {
    // Update the state of the side panel or full screen calendar.
    const state = getState();
    const appPanel = makeGetAppPanel<void>(getFirstAppPanelId)(state);

    const isViewingFullScreenCalendar = appPanel.contentType === AppContentTypesEnum.CALENDARS;
    const calendarsPanelId = isViewingFullScreenCalendar
      ? getFirstAppCalendarsPanelId(state.panels)
      : getFirstCalendarsSidePanelId(state.panels);
    if (!calendarsPanelId) {
      return;
    }

    // Get the calendar provider type for analytics purposes.
    const calendarVersion = getCalendarVersions(state)[eventParameters.calendarVersionId];
    if (!calendarVersion) {
      return;
    }
    const calendar = getCalendars(state)[calendarVersion.calendarId];
    if (!calendar) {
      return;
    }

    const parameters = {
      calendar_type: isViewingFullScreenCalendar ? CalendarTypesEnum.LARGE : CalendarTypesEnum.SIDE,
      calendar_provider: calendar.provider,
    };
    trackInterfaceEvent(InterfaceEventsEnum.OPEN_CALENDAR_EVENT, parameters);

    const eventGridParameters: CalendarEventGridParameters = {
      ...eventParameters,
      source: FocusedEventSourcesEnum.CALENDAR_GRID,
      overrides: {},
    };

    batch(() => {
      dispatch(updateCalendarFocusedDateAction(calendarsPanelId, focusedDate));
      dispatch(
        isViewingFullScreenCalendar
          ? popCalendarEventAction(calendarsPanelId, eventGridParameters)
          : focusCalendarEventAction(calendarsPanelId, eventGridParameters),
      );
    });
  };
}

export function openCalendarEventInSidePanelAction(
  focusedDate: number,
  eventParameters: CalendarEventParameters,
): ThunkAction {
  return async (dispatch, getState) => {
    // Open the side panel calendar if needed.
    const existingCalendarSidePanel = getCalendarSidePanel(getState());
    const isSidePanelCalendarVisible = getIsSidePanelCalendarVisible(getState());
    if (!existingCalendarSidePanel || !isSidePanelCalendarVisible) {
      dispatch(updateSelectedSideFrameIdAction(FrameTypesEnum.CALENDAR));
    }

    // TEMPORARY: Wait for the panel to be created.
    // This won't be needed once the panels are created in action creators.
    if (!existingCalendarSidePanel) {
      await delayAsync(300);
    }

    // We should now be able to find it in the state.
    const calendarSidePanel = getCalendarSidePanel(getState());
    if (!calendarSidePanel) {
      throw new Error('Side calendar panel not found.');
    }

    // Build the new selectedCalendarVersionIds.
    const selectedCalendarVersionIds = chain(calendarSidePanel.parameters.selectedCalendarVersionIds)
      .concat(eventParameters.calendarVersionId)
      .uniq()
      .value();

    // Build the new parameters to navigate to.
    const newParameters: CalendarsParameters = {
      ...calendarSidePanel.parameters,
      focusedDate,
      selectedCalendarVersionIds,
    };

    const eventGridParameters: CalendarEventGridParameters = {
      ...eventParameters,
      source: FocusedEventSourcesEnum.CALENDAR_GRID,
      overrides: {},
    };

    batch(() => {
      dispatch(navigateExistingCalendarsAction(calendarSidePanel.id, newParameters));
      dispatch(highlightCalendarEventAction(calendarSidePanel.id, eventGridParameters));
    });
  };
}

// TODO: Find the main window side panel calendar using its deterministic panel ID.
export function getFirstCalendarsSidePanelId(panels: PanelsState) {
  return _(panels)
    .values()
    .map((panel) => (panel?.type === PanelTypesEnum.CALENDARS ? panel.id : undefined))
    .compact()
    .find(isCalendarsSidePanelId);
}

/*
 * Viewing now calendars.
 */

export function updateViewingNowCalendarsAction(
  panelId: CalendarsPanelId,
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
): ThunkAction {
  return (dispatch, getState) => {
    batch(() => {
      const parameters = makeGetCalendarsParameters()(getState(), {panelId});

      // Update the viewing now calendars.
      const updateAction: UpdateVisibleCalendarsAction = {
        type: CalendarsPanelActionsEnum.UPDATE_VISIBLE_CALENDARS,
        payload: {
          panelId,
          viewingNowCalendars,
          selectedCalendarVersionIds: parameters.selectedCalendarVersionIds,
        },
      };
      dispatch(updateAction);

      // If needed, navigate. This ensures the new set of viewingNowCalendars are displayed for the desired panel.
      const viewingNowCalendarVersionIds = _(viewingNowCalendars)
        .map((c) => c.calendarVersionId)
        .compact()
        .value();

      const newParameters: CalendarsParameters = {
        ...parameters,
        viewingNowCalendarVersionIds,
      };
      dispatch(navigateExistingCalendarsAction(panelId, newParameters));
    });
  };
}

export function addViewingNowCalendarVersionForAddressAction(
  panelId: CalendarsPanelId,
  address: string,
): ThunkAction {
  return async (dispatch, getState) => {
    // Check if we have an existing calendarVersion that matches this address.
    const calendarVersion = dispatch(findPrimaryOwnerCalendarVersionAction(address));
    // TODO: Check if our access role is at least as high as the team access role.

    // Create an entry in the viewing now calendars list if we don't already have one.
    const {id} = dispatch(findOrCreateViewingNowCalendar(panelId, address, calendarVersion));

    // Dispatch the action that processes the new calendar action.
    dispatch(addCalendarAddressAction(panelId, address));

    // If we already had a CalendarVersion, force sync the calendarVersion to make sure we have up-to-date events.
    if (calendarVersion) {
      dispatch(calendarVersionForcedOnceTimeSyncAction(calendarVersion.id));
      return;
    }

    // Otherwise, fetch from the server.
    try {
      const newCalendarVersion = await dispatch(fetchCalendarVersionByAddressAsyncAction(address));
      dispatch(
        updateViewingNowCalendar(panelId, id, {
          calendarVersionId: newCalendarVersion && newCalendarVersion.id,
          status: newCalendarVersion ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.FAILED,
        }),
      );
      if (newCalendarVersion) {
        dispatch(calendarVersionForcedOnceTimeSyncAction(newCalendarVersion.id));
      }
    } catch (error) {
      reportToBugsnag(new ExtendedError('Error while fetching a ViewingNowCalendar.', error));
      dispatch(updateViewingNowCalendar(panelId, id, {status: AsyncStatusesEnum.FAILED}));
    }
  };
}

export function updateViewingNowCalendarsForParticipantsChangeAction(
  panelId: CalendarsPanelId,
  oldVisualParticipants: ReadonlyArray<VisualParticipantModel>,
  newVisualParticipants: ReadonlyArray<VisualParticipantModel>,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    const {viewingNowCalendars} = makeGetCalendarsPanel()(state, {panelId});

    // Remove any viewing now calendars that correspond to removed visual participants.
    const newContactUids = newVisualParticipants.map((p) => p.contact.uid);
    const removedAddresses = oldVisualParticipants
      .filter((p) => !newContactUids.includes(p.contact.uid))
      .map((p) => p.contact.handle);
    const newViewingNowCalendars = viewingNowCalendars.filter((v) => !removedAddresses.includes(v.address));

    // Add any viewing now calendars that correspond to added visual participants.
    const oldContactUids = oldVisualParticipants.map((p) => p.contact.uid);
    const addedAddresses = newVisualParticipants
      .filter((p) => !oldContactUids.includes(p.contact.uid))
      .map((p) => p.contact.handle);

    batch(() => {
      dispatch(updateViewingNowCalendarsAction(panelId, newViewingNowCalendars));

      addedAddresses.forEach((address) => {
        dispatch(addViewingNowCalendarVersionForParticipantAction(panelId, address));
      });
    });
  };
}

export function addViewingNowCalendarVersionForParticipantAction(
  panelId: CalendarsPanelId,
  address: string,
): ThunkAction {
  return async (dispatch) => {
    // If we already have a CalendarVersion, create the ViewingNowCalendar.
    const calendarVersion = dispatch(findPrimaryOwnerCalendarVersionAction(address));
    if (calendarVersion) {
      dispatch(findOrCreateViewingNowCalendar(panelId, address, calendarVersion));
      return;
    }

    // Otherwise, fetch from the server.
    try {
      const newCalendarVersion = await dispatch(fetchCalendarVersionByAddressAsyncAction(address));
      if (!newCalendarVersion) {
        return;
      }

      dispatch(findOrCreateViewingNowCalendar(panelId, address, newCalendarVersion));
    } catch (error) {
      reportToBugsnag(new ExtendedError('Error while fetching a ViewingNowCalendar.', error));
    }
  };
}

function findPrimaryOwnerCalendarVersionAction(
  address: string,
): ThunkAction<CalendarVersionModel | undefined> {
  return (_dispatch, getState) => {
    const calendars = getCalendars(getState());
    const flatCalendarVersions = getFlatCalendarVersions(getState());
    return flatCalendarVersions.find((v) => {
      const calendar = calendars[v.calendarId];
      return Boolean(calendar && calendar.isPrimaryForOrganizer && calendar.organizerAddress === address);
    });
  };
}

export function findOrCreateViewingNowCalendar(
  panelId: CalendarsPanelId,
  address: string,
  calendarVersion: CalendarVersionModel | undefined,
): ThunkAction<ViewingNowCalendar> {
  return (dispatch, getState) => {
    const state = getState();
    const calendarVersions = getCalendarVersions(state);
    const calendarsPanel = makeGetCalendarsPanel()(state, {panelId});
    const {viewingNowCalendars, parameters} = calendarsPanel;
    const selectedCalendarVersions = _(parameters.selectedCalendarVersionIds)
      .map((id) => calendarVersions[id])
      .compact()
      .value();

    // If we already have a ViewingNowCalendar, return it.
    const existingViewingNowCalendar = viewingNowCalendars.find((c) => c.address === address);
    if (existingViewingNowCalendar) {
      return existingViewingNowCalendar;
    }

    // Otherwise, create it.
    const newViewingNowCalendar = createViewingNowCalendar(
      address,
      calendarVersion,
      viewingNowCalendars,
      selectedCalendarVersions,
    );
    const newViewingNowCalendars = viewingNowCalendars.concat([newViewingNowCalendar]);
    dispatch(updateViewingNowCalendarsAction(panelId, newViewingNowCalendars));
    return newViewingNowCalendar;
  };
}

export function updateViewingNowCalendar(
  panelId: CalendarsPanelId,
  id: string,
  patch: Partial<ViewingNowCalendar>,
): ThunkAction {
  return (dispatch, getState) => {
    const {viewingNowCalendars} = makeGetCalendarsPanel()(getState(), {panelId});
    const newViewingNowCalendars = viewingNowCalendars.map((c) => {
      if (c.id === id) {
        return {...c, ...patch};
      }

      return c;
    });

    dispatch(updateViewingNowCalendarsAction(panelId, newViewingNowCalendars));
  };
}

export function fetchCalendarVersionByAddressAsyncAction(
  address: string,
): ThunkAsyncAction<CalendarVersionModel | undefined> {
  return async (dispatch, getState) => {
    const company = getCompany(getState());
    const namespace = getUserNamespace(getState());

    const dataGroup = await fetchCalendarForOrganizerAddressAsync(company.url, namespace, address);
    if (!dataGroup) {
      return undefined;
    }

    const calendarVersion = dataGroup.calendarVersions && atIndex(dataGroup.calendarVersions, 0);
    if (!calendarVersion) {
      throw new ErrorWithData('Calendar Version not found in request result.', {namespace, address});
    }

    dispatch(updateDataAction(dataGroup));
    return calendarVersion;
  };
}

export function createViewingNowCalendar(
  address: string,
  calendarVersion: CalendarVersionModel | undefined,
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
  selectedCalendarVersions: ReadonlyArray<CalendarVersionModel>,
) {
  return {
    id: randomString(),
    address,
    calendarVersionId: calendarVersion?.id,
    status: calendarVersion ? AsyncStatusesEnum.LOADED : AsyncStatusesEnum.LOADING,
    color: findViewingNowCalendarColor(calendarVersion, viewingNowCalendars, selectedCalendarVersions),
  };
}

function findViewingNowCalendarColor(
  calendarVersion: CalendarVersionModel | undefined,
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
  selectedCalendarVersions: ReadonlyArray<CalendarVersionModel>,
) {
  // If the calendarVersion is also being displayed in the My Calendars list, use the same color for consistency.
  if (calendarVersion && !calendarVersion.isHidden) {
    return calendarVersion.color;
  }

  // Pick the first available color or a random one.
  const takenColors = _(selectedCalendarVersions)
    .map((v) => v.color)
    .concat(viewingNowCalendars.map((v) => v.color))
    .uniq()
    .value();
  const firstAvailableColor = first(difference(sortedCalendarColors, takenColors));

  return firstAvailableColor || sortedCalendarColors[random(sortedCalendarColors.length - 1)];
}

// TODO: Find the main window full screen calendar using its deterministic panel ID.
function getFirstAppCalendarsPanelId(panels: PanelsState) {
  return _(panels)
    .values()
    .map((panel) => (panel?.type === PanelTypesEnum.CALENDARS ? panel.id : undefined))
    .compact()
    .find(isAppCalendarsPanelId);
}

function buildNavigateActionData(
  panel: CalendarsPanelState,
  parameters: CalendarsParameters,
): {
  navigateAction: NavigateCalendarsAction;
  cancelToken: CancelToken;
} {
  const [cancelTokenId, cancelToken] = buildPanelCancelToken(panel.id, panel.cancelTokenId);
  const navigateAction = {
    type: CalendarsPanelActionsEnum.NAVIGATE_CALENDARS as const,
    payload: {panelId: panel.id, parameters, cancelTokenId},
  };
  return {navigateAction, cancelToken};
}

/*
 * Union type.
 */

export type CalendarsPanelAction =
  | CalendarsPanelSchedulingAction
  | NavigateCalendarsAction
  | FetchCalendarEventVersionsCompletedAction
  | FetchCalendarEventVersionsFailedAction
  | HighlightCalendarEventAction
  | FocusCalendarEventAction
  | PopCalendarEventAction
  | UpdateVisibleCalendarsAction
  | AddCalendarAddressAction;
